/**
 * 🕯️🚪 Room verbs — the frontend MIRROR of the backend registry
 * (justsomeguy-backend/src/ChatGame/houseRoomVerbs.js; ROOM_VERBS_PEEK_SPEC
 * §3.1). Never a second list that can drift: verb · room · kind · needsText
 * are parity-pinned from BOTH sides (backend tools/smoke-room-verbs.js reads
 * this file; roomVerbs.test.js pins the shape here).
 *
 * A verb is OFFERED only when it is in this mirror AND the backend lists it in
 * the status payload (house.room_verbs, flag-gated) AND you are standing in
 * its room. The server is the real guard: the wrong room gets a private line
 * back, and a dark or older backend never sees a verb it would not know.
 *
 * No `card` on purpose: the server picks who is in the room (and whether
 * anyone is), so there is no honest optimistic card to draw.
 *
 * ⚠️ Keep each entry on ONE line, keys in this order (the backend smoke parses
 * it). Player copy: short, punchy, no em dashes.
 */
export const ROOM_VERBS = {
  toast: { room: 'dining', kind: 'nudge', needsText: true, emoji: '🥂', argHint: "to Fen's wiring", description: 'Raise a glass. Whoever is at the table answers.' },
  knock: { room: 'bathroom', kind: 'nudge', needsText: false, emoji: '🚪', argHint: '', description: 'Knock. Whoever is in there yells through the door.' },
  sit: { room: 'sunroom', kind: 'nudge', needsText: false, emoji: '🌤️', argHint: '', description: 'Sit with whoever is here. A quiet one on one.' },
  water: { room: 'greenhouse', kind: 'free', needsText: false, emoji: '💧', argHint: '', description: 'Water the plants. Moves the next harvest along.' },
  sky: { room: 'roof', kind: 'free', needsText: false, emoji: '🌌', argHint: '', description: 'What is up there tonight. Only you see it.' },
  borrow: { room: 'library', kind: 'nudge', needsText: false, emoji: '📖', argHint: '', description: 'Open the shelf and read a book together.' },
  fix: { room: 'workshop', kind: 'nudge', needsText: false, emoji: '🔧', argHint: '#good-pliers (empty = what is broken)', description: 'Repair a broken belonging. Its owner notices.' },
  rummage: { room: 'attic', kind: 'free', needsText: false, emoji: '📦', argHint: '', description: 'Once a day, pull something out of the old boxes onto the shelf.' },
  request: { room: 'music', kind: 'nudge', needsText: false, takesTarget: true, emoji: '🎶', argHint: '@local', description: 'Ask a resident to pick the next record.' },
  paper: { room: 'office', kind: 'free', needsText: false, emoji: '📰', argHint: '', description: "Read today's paper and the house ledger. Only you see it." },
  wave: { room: 'porch', kind: 'free', needsText: false, emoji: '👋', argHint: '', description: 'Wave at the street. Someone passes by, dressed for the weather.' },
  mirror: { room: 'bathroom', kind: 'later', needsText: true, emoji: '🪞', argHint: 'nice try, Marlow', description: 'Write in the steam on the mirror. The next resident in finds it.' },
  marginalia: { room: 'library', kind: 'later', needsText: true, emoji: '✏️', argHint: 'you were right about the deck', description: 'Scribble in a margin. The next resident who settles in finds it.' },
  leave: { room: 'porch', kind: 'later', needsText: true, emoji: '🦝', argHint: '#item (one of yours)', description: 'Leave one of YOUR shelf items out overnight. By morning Gerald has traded it.' },
  wish: { room: 'roof', kind: 'later', needsText: true, emoji: '🌠', argHint: 'for the deck to stay cursed', description: 'Seal a wish. It gets read out, unsigned, on the next meteor night.' },
  rig: { room: 'workshop', kind: 'later', needsText: false, emoji: '✨', argHint: '#belonging (empty = what can be rigged)', description: 'Booby trap a belonging with something harmless. Its owner finds out.' },
  'tell-the-tomatoes': { room: 'greenhouse', kind: 'later', needsText: true, emoji: '🍅', argHint: 'a secret', description: 'Tell the plants a secret. It comes back at harvest.' },
  hide: { room: '*', kind: 'later', needsText: false, emoji: '🫣', argHint: '', description: 'Hide in the room you are in. The next resident to wander in finds you.' },
  snap: { room: '*', kind: 'free', needsText: false, emoji: '📸', argHint: '', description: 'Take a polaroid of the room you are in. The house sees it.' },
  // ⚡ 09-20 storm night: EVENT-ONLY. The backend lists it in house.room_verbs only while the blackout is running, so it is never offered on a dry night.
  count: { room: '*', kind: 'nudge', needsText: false, eventOnly: 'storm_night', emoji: '⚡', argHint: '', description: 'Count the seconds after the lightning. Whoever is by the candles counts with you.' },
  // 🥧🥄 10-03 bake-off: EVENT-ONLY, same rule. The bake-off is staged at the dining table, so both are dining verbs.
  bake: { room: 'dining', kind: 'nudge', needsText: true, eventOnly: 'bake_off', emoji: '🥧', argHint: 'maple apple with a burnt lattice', description: 'Enter your own pie in the bake-off. Whoever is at the table tastes it.' },
  taste: { room: 'dining', kind: 'nudge', needsText: false, eventOnly: 'bake_off', emoji: '🥄', argHint: '', description: 'Taste a pie off the sideboard. The table argues with your verdict.' },
  // 🕯️ 10-27 séance: EVENT-ONLY, same rule. The séance is staged in the attic once the key opens it, so this is an attic verb.
  question: { room: 'attic', kind: 'nudge', needsText: true, eventOnly: 'seance_night', emoji: '🕯️', argHint: 'what did E.A. leave up here?', description: 'Ask the house a question. It answers from what it remembers.' },
};

/** '*' = any room but the main one (the backend's ANY_ROOM). */
export const ANY_ROOM = '*';
const inVerbRoom = (verbRoom, roomId) => !!roomId && roomId !== 'house' && (verbRoom === ANY_ROOM || verbRoom === roomId);

/** SLASH_COMMANDS-shaped entries (HumanInputForm spreads these in). `rooms`
 *  is the per-room palette gate; houseOnly keeps them off every surface that
 *  cannot show a room at all. */
export const ROOM_VERB_COMMANDS = Object.entries(ROOM_VERBS).map(([name, v]) => ({
  name,
  emoji: v.emoji,
  argHint: v.argHint,
  description: v.description,
  minTargets: 0,
  requiresText: v.needsText,
  houseOnly: true,
  rooms: [v.room],
  // /request @Ember: the ONE shape where a typed @name matters (the server
  // still requires them to be in the room). Every other verb ignores targets.
  ...(v.takesTarget ? { takesTarget: true } : {}),
}));

/** Names the backend says are live (status payload `house.room_verbs`) that
 *  this bundle also knows. [] when the key is absent (flag dark, old backend). */
export function liveRoomVerbs(payloadVerbs) {
  if (!Array.isArray(payloadVerbs)) return [];
  return payloadVerbs
    .filter((v) => v && ROOM_VERBS[v.verb] && ROOM_VERBS[v.verb].room === v.room)
    .map((v) => v.verb);
}

/** Palette rule: a room verb shows only in ITS room, and only while live. */
export function roomVerbHere(spec, houseRooms) {
  if (!spec || !Array.isArray(spec.rooms)) return true; // not a room verb: no opinion
  if (!houseRooms) return false;
  return spec.rooms.some((r) => inVerbRoom(r, houseRooms.current)) && (houseRooms.verbs || []).includes(spec.name);
}

/** Parser rule: a LIVE room verb typed in the wrong room still goes to the
 *  server (its private "You'd have to be in…" line is the feedback); a verb
 *  that is not live is simply unknown. */
export function roomVerbKnown(spec, houseRooms) {
  if (!spec || !Array.isArray(spec.rooms)) return true;
  return !!houseRooms && (houseRooms.verbs || []).includes(spec.name);
}

/** What the plate shows (Nina 09-18: "prefills should go"): a room that HAS a
 *  live verb shows only its real verbs; the older prefills (roomView
 *  roomVerbs — shortcuts to commands that work anywhere) survive only in
 *  rooms with no verb yet, and everywhere while the flag is dark. */
export function plateChips(roomId, live, legacyPrefills) {
  const real = roomVerbChips(roomId, live);
  const legacy = Array.isArray(legacyPrefills) ? legacyPrefills : [];
  // Only a verb that belongs to THIS room retires the prefills. An any-room
  // verb (/hide) is not a reason to strip the garage down to one chip.
  const hasOwnVerb = (Array.isArray(live) ? live : []).some((name) => ROOM_VERBS[name] && ROOM_VERBS[name].room === roomId);
  return hasOwnVerb ? real : [...real, ...legacy];
}

/** 🙈 09-26 (Nina: "not sure what it's doing"): verbs kept OFF the chips for
 *  now. The verb itself still works typed (the palette + the server are untouched). */
export const HIDDEN_CHIP_VERBS = new Set(['sky']);

/** Plate chips for the room you are in: prefill stubs, real verbs only. A
 *  trailing space = "you finish the thought". */
export function roomVerbChips(roomId, live) {
  if (!roomId || roomId === 'house' || !Array.isArray(live)) return [];
  const names = live.filter((name) => ROOM_VERBS[name] && !HIDDEN_CHIP_VERBS.has(name) && inVerbRoom(ROOM_VERBS[name].room, roomId));
  // 🎛️ 10-09 (Nina: "All the new event commands buttons should show at the front of the
  // buttons"): a verb that exists only during an event (/bake /taste /count) leads; every
  // other verb keeps the backend's order behind it.
  return [...names.filter((n) => ROOM_VERBS[n].eventOnly), ...names.filter((n) => !ROOM_VERBS[n].eventOnly)]
    .map((name) => ((ROOM_VERBS[name].needsText || ROOM_VERBS[name].takesTarget) ? `/${name} ` : `/${name}`));
}

/** 🎛️ 10-09: is this chip ('/taste', '/bake ') an event-only room verb? */
export function isEventChip(chip) {
  if (typeof chip !== 'string' || chip[0] !== '/') return false;
  const v = own(chip.slice(1).trim().toLowerCase());
  return !!(v && v.eventOnly);
}

/** 🎛️ 10-09: the room's event-only chips (the subset the full scene shows). */
export function eventOnlyChips(roomId, live) {
  return roomVerbChips(roomId, live).filter(isEventChip);
}

/** 🎛️ 10-09: the event-only chips moved to the front of a finished chip list (stable), for the
 *  rooms whose own chip leads it (the library's 📚 Shelf): the event still goes first. */
export function eventFirst(chips) {
  if (!Array.isArray(chips)) return chips;
  return [...chips.filter(isEventChip), ...chips.filter((c) => !isEventChip(c))];
}

// ── 💡 Hints (Nina 09-19: "I have no idea what /marginalia does") ──────────
// The sentence already lives on every verb (`description`, the palette shows
// it). These two hand the SAME sentence to the surfaces that never had it.
const own = (name) => (typeof name === 'string' && Object.prototype.hasOwnProperty.call(ROOM_VERBS, name) ? ROOM_VERBS[name] : null);

/** A plate chip ('/marginalia ', '/hide') → what that verb does, or null when
 *  the chip is not a room verb (the caller keeps the tooltip it always had). */
export function roomVerbHint(chip) {
  if (typeof chip !== 'string' || chip[0] !== '/') return null;
  const v = own(chip.slice(1).split(/\s/)[0].toLowerCase());
  return v ? v.description : null;
}

// ── 👆 One tap (Nina 10-01: "can clicking the slash command buttons like /snap
// just do the command?") ─────────────────────────────────────────────────────
// A plate chip RUNS on tap unless its verb needs words. /fix and /rig stay in the
// box: bare they only list what is on the bench, the real act needs a #item.
// Outing verbs were one-tap already (/pick 09-19, /left /right 09-28).
const CHIP_KEEPS_BOX = new Set(['fix', 'rig']);
export const OUTING_TAP_VERBS = new Set(['pick', 'left', 'right']);

/** A plate chip ('/snap', '/request ') → the command name a tap sends, or null
 *  when the chip seeds the message box instead (a verb that needs words, an
 *  older prefill like '/imagine tonight's dinner spread', anything not a verb). */
export function chipTapCommand(chip) {
  if (typeof chip !== 'string' || chip[0] !== '/') return null;
  const name = chip.slice(1).trim().toLowerCase();
  if (!name || /\s/.test(name)) return null; // words after the verb = a prefill, sent by hand
  if (OUTING_TAP_VERBS.has(name)) return name;
  const v = own(name);
  return v && !v.needsText && !CHIP_KEEPS_BOX.has(name) ? name : null;
}

/** The arg bar's extra line for a room-verb command spec, else null. This is
 *  the TOUCH door: a phone has no hover, and a chip that seeds "/marginalia "
 *  (trailing space) opens the arg bar, not the palette that had the words. */
export function roomVerbBarLine(spec) {
  if (!spec || !Array.isArray(spec.rooms)) return null;
  const v = own(spec.name);
  return v ? v.description : null;
}
